import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import PortfolioManager from './components/PortfolioManager';
import InquiriesManager from './components/InquiriesManager';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<'portfolio' | 'inquiries'>('inquiries');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      window.REACT_APP_NAVIGATE('/admin');
      return;
    }
    
    setIsLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.REACT_APP_NAVIGATE('/admin');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-cyan-600 animate-spin"></i>
          <p className="text-gray-600 mt-4">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <i className="ri-dashboard-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-xs text-gray-600">Manage your website content</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-cyan-600 transition-colors flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                View Website
              </a>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-all flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-box-line"></i>
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`px-6 py-4 font-semibold text-sm transition-all relative whitespace-nowrap cursor-pointer ${
                activeTab === 'inquiries'
                  ? 'text-cyan-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-mail-line mr-2"></i>
              Customer Inquiries
              {activeTab === 'inquiries' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`px-6 py-4 font-semibold text-sm transition-all relative whitespace-nowrap cursor-pointer ${
                activeTab === 'portfolio'
                  ? 'text-cyan-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-image-line mr-2"></i>
              Portfolio Management
              {activeTab === 'portfolio' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {activeTab === 'inquiries' && <InquiriesManager />}
        {activeTab === 'portfolio' && <PortfolioManager />}
      </main>
    </div>
  );
}
